.container {
  width: 100%;
  max-width: var(--width-page);
  margin-inline: auto;
  padding-inline: var(--space-5);
}

/* Button */

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: 2.5rem;
  padding: 0 var(--space-4);
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  transition:
    background var(--transition),
    border-color var(--transition),
    color var(--transition);
}

.button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.small {
  min-height: 2rem;
  padding: 0 var(--space-3);
  font-size: var(--text-xs);
}

.primary {
  background: var(--color-accent-solid);
  color: var(--color-accent-contrast);
}

.primary:hover:not(:disabled) {
  background: var(--color-accent-solid-hover);
  color: var(--color-accent-contrast);
}

.secondary {
  background: var(--color-bg);
  border-color: var(--color-border-strong);
  color: var(--color-text);
}

.secondary:hover:not(:disabled) {
  background: var(--color-bg-subtle);
}

.ghost {
  background: transparent;
  color: var(--color-text-muted);
}

.ghost:hover:not(:disabled) {
  background: var(--color-bg-inset);
  color: var(--color-text);
}

/* Badge */

.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  /* On the grid, and a shade off centre: the block padding gives the top back to the bottom so
     the label sits on the middle of the badge rather than a little under it. */
  padding-inline: var(--space-2);
  padding-block: calc(var(--space-1) - var(--label-optical))
    calc(var(--space-1) + var(--label-optical));
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  line-height: 1.25;
  white-space: nowrap;
}

.tone-neutral {
  background: var(--color-bg-subtle);
  color: var(--color-text-muted);
}

/* What a badge says that is not a status: a count, or that a thing is part of another. It takes
   the point colour, which is the one warm thing on the page and so reads before anything around
   it -- and which is not the accent, so a badge is never mistaken for something to click. */
.tone-point {
  background: var(--color-point-subtle);
  border-color: transparent;
  color: var(--color-point);
}

.tone-success {
  background: var(--color-success-subtle);
  border-color: transparent;
  color: var(--color-success);
}

/* A state, and so not a filled pill: the point badge is the one filled warm pill on a page, and a
   warning filled in a near hue read as the same thing. Here the warm colour is only on the mark,
   the words stay in the page's own grey, and the line round it is a line and not a fill. */
.tone-warning {
  background: var(--color-bg-subtle);
  border-color: var(--color-border-strong);
  color: var(--color-text-muted);
}

.tone-warning svg {
  flex: none;
  color: var(--color-warning);
}

.tone-danger {
  background: var(--color-danger-subtle);
  border-color: transparent;
  color: var(--color-danger);
}

/* Avatar: the owner's picture, a rounded square on the inset surface, so that a logo with a
   transparent background sits on something and a photo keeps a frame. */

.avatar {
  flex: none;
  aspect-ratio: 1;
  object-fit: cover;
  border: var(--border);
  border-radius: 22%;
  background: var(--color-bg-inset);
}

.avatar-sm {
  width: 1.5rem;
}

.avatar-md {
  width: 2.5rem;
}

.avatar-lg {
  width: 4.5rem;
}

/* Verified mark: sits next to a name, at the size of its text. */

.verified {
  display: inline-flex;
  vertical-align: middle;
  color: var(--color-accent);
  font-size: 0.85em;
}

.verified svg {
  display: block;
}

.verified path {
  stroke: var(--color-accent-contrast);
}

/* Callout */

.callout {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-4);
  /* The tone is the whole hairline, not a thick stripe down one side. */
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-bg-subtle);
  font-size: var(--text-sm);
}

.callout-warning {
  border-color: var(--color-warning);
  background: var(--color-warning-subtle);
}

.callout-danger {
  border-color: var(--color-danger);
  background: var(--color-danger-subtle);
}

.calloutTitle {
  font-weight: var(--weight-bold);
}

.calloutBody {
  color: var(--color-text-muted);
}

.calloutTitle + .calloutBody {
  margin-top: var(--space-1);
}

/* A callout that says two things keeps them a little apart. */
.calloutBody p + p {
  margin-top: var(--space-2);
}

/* Spinner */

.spinner {
  display: inline-flex;
}

.spinnerRing {
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--color-border-strong);
  border-top-color: var(--color-accent);
  border-radius: 50%;
}

@media (prefers-reduced-motion: no-preference) {
  .spinnerRing {
    animation: spin 700ms linear infinite;
  }

  .skeletonLine {
    animation: pulse 1.4s ease-in-out infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Skeleton */

.skeleton {
  display: grid;
  gap: var(--space-3);
}

.skeletonLine {
  display: block;
  height: 0.9rem;
  border-radius: var(--radius-sm);
  background: var(--color-bg-inset);
}

.skeletonLine:nth-child(3n) {
  width: 62%;
}

.skeletonLine:nth-child(3n + 1) {
  width: 88%;
}

@keyframes pulse {
  50% {
    opacity: 0.5;
  }
}

/* Empty state */

.empty {
  padding: var(--space-7) var(--space-5);
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-lg);
  text-align: center;
}

.emptyTitle {
  font-weight: var(--weight-bold);
}

.emptyBody {
  max-width: 34rem;
  margin: var(--space-2) auto 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

/* Section */

.section {
  padding-block: var(--space-9);
  border-top: var(--border);
}

.sectionSubtle {
  background: var(--color-bg-subtle);
}

.sectionTitle {
  font-size: var(--text-2xl);
  text-align: center;
}

.sectionLead {
  max-width: var(--width-prose);
  margin-top: var(--space-3);
  margin-inline: auto;
  text-align: center;
  color: var(--color-text-muted);
  font-size: var(--text-lg);
}

/* The title and its lead are one block; what follows them stands clear of it. */
.sectionTitle + *:not(.sectionLead),
.sectionLead + * {
  margin-top: var(--space-7);
}

@media (max-width: 40rem) {
  .container {
    padding-inline: var(--space-4);
  }

  .section {
    padding-block: var(--space-8);
  }
}
